import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useNavigate } from 'react-router-dom';
type Phase = 'baseline-collection' | 'work-baseline-output' | 'work-calibration' | 'integrated-work-output';
export function QuickWorkDecode() {
  const navigate = useNavigate();
  const [isAuthenticated] = useState(true);
  const [showLoginModal, setShowLoginModal] = useState(!isAuthenticated);
  const [hasBaseline] = useState(false);
  const [currentPhase, setCurrentPhase] = useState<Phase>(hasBaseline ? 'work-baseline-output' : 'baseline-collection');
  const [showExplanation, setShowExplanation] = useState(false);
  const [showWhatChanged, setShowWhatChanged] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatingMessage, setGeneratingMessage] = useState('');
  // Baseline data (reusing QuickSelfDecode structure)
  const [baselineData, setBaselineData] = useState({
    dateOfBirth: '1990-01-15',
    timeOfBirth: '',
    timeKnown: 'not-sure',
    placeOfBirth: '',
    country: '',
    year: '1990',
    month: '01',
    day: '15',
    hour: '12',
    minute: '00',
    period: 'AM'
  });
  // Work calibration data
  const [workCalibrationData, setWorkCalibrationData] = useState({
    pressureLevel: '',
    frictionZones: [] as string[],
    pressureMode: '',
    workContext: ''
  });
  // Work baseline output (structural translation)
  const workBaselineOutput = {
    workOrientation: 'You naturally operate with a preference for autonomy over constant guidance, structure over open-ended ambiguity, and depth over speed. You tend to work behind-the-scenes rather than in high-visibility roles, building systems and clarity before executing. This is not learned behavior — it is how this structure engages with work systems.',
    authorityResponse: 'Under authority and pressure, this system stabilizes by creating internal structure. It does not resist authority directly but may withdraw into analysis when directives feel unclear or misaligned. Over-functioning can occur when the system perceives chaos that needs organizing. The response is structural, not emotional.',
    workBlindSpot: 'This pattern can create friction when speed is prioritized over depth, or when visibility and quick output are rewarded more than systematic thinking. The system may delay action while seeking clarity that work environments do not always provide.'
  };
  // Integrated work output
  const integratedWorkOutput = {
    structuralBaseline: 'Structure-driven stabilizer. Prefers autonomy, depth, and behind-the-scenes system-building. Responds to pressure by creating internal clarity.',
    currentExpression: 'Right now, this pattern is running at high intensity, primarily activated by authority friction and ambiguity of role. You are aware of it in the moment, which creates space for recalibration rather than automatic response. The system is stabilizing under pressure but may be over-functioning to compensate for unclear directives.',
    workTensionSnapshot: 'A structure-driven stabilizer operating under high authority friction and role ambiguity.',
    whatChanged: {
      baseline: 'The baseline showed your structural work orientation (autonomy, depth, behind-the-scenes), authority response (stabilizing through internal structure), and blind spot (friction when speed trumps depth).',
      workContext: 'The work calibration revealed: current pressure level (high), specific friction zones (authority and role ambiguity), and pressure response mode (taking more control). This shows how the baseline pattern is expressing in your current work environment.',
      whyBaselineAlone: 'Baseline shows how you structurally operate at work. Work context shows where that pattern is activating right now, at what intensity, and under what specific conditions. Baseline is structure; work context is current expression.'
    }
  };
  const currentYear = new Date().getFullYear();
  const years = Array.from({
    length: currentYear - 1919
  }, (_, i) => currentYear - i);
  const months = [{
    value: '01',
    label: 'January'
  }, {
    value: '02',
    label: 'February'
  }, {
    value: '03',
    label: 'March'
  }, {
    value: '04',
    label: 'April'
  }, {
    value: '05',
    label: 'May'
  }, {
    value: '06',
    label: 'June'
  }, {
    value: '07',
    label: 'July'
  }, {
    value: '08',
    label: 'August'
  }, {
    value: '09',
    label: 'September'
  }, {
    value: '10',
    label: 'October'
  }, {
    value: '11',
    label: 'November'
  }, {
    value: '12',
    label: 'December'
  }];
  const getDaysInMonth = (year: string, month: string) => {
    if (!year || !month) return 31;
    return new Date(parseInt(year), parseInt(month), 0).getDate();
  };
  const updateDateOfBirth = (year: string, month: string, day: string) => {
    if (year && month && day) {
      const dateString = `${year}-${month}-${day}`;
      setBaselineData(prev => ({
        ...prev,
        year,
        month,
        day,
        dateOfBirth: dateString
      }));
    } else {
      setBaselineData(prev => ({
        ...prev,
        year,
        month,
        day,
        dateOfBirth: ''
      }));
    }
  };
  const convertTo24Hour = (hour: string, period: string): string => {
    let h = parseInt(hour);
    if (period === 'PM' && h !== 12) h += 12;
    if (period === 'AM' && h === 12) h = 0;
    return h.toString().padStart(2, '0');
  };
  const updateTimeOfBirth = (hour: string, minute: string, period: string) => {
    const hour24 = convertTo24Hour(hour, period);
    const timeString = `${hour24}:${minute}`;
    setBaselineData(prev => ({
      ...prev,
      hour,
      minute,
      period,
      timeOfBirth: timeString
    }));
  };
  const handleBaselineSubmit = () => {
    if (!baselineData.dateOfBirth || !baselineData.placeOfBirth || !baselineData.country) return;
    if ((baselineData.timeKnown === 'exact' || baselineData.timeKnown === 'approximate') && !baselineData.timeOfBirth) return;
    setIsGenerating(true);
    setGeneratingMessage('Generating your structural baseline...');
    setTimeout(() => {
      setIsGenerating(false);
      setCurrentPhase('work-baseline-output');
    }, 2000);
  };
  const handleWorkCalibrationSubmit = () => {
    if (!workCalibrationData.pressureLevel || workCalibrationData.frictionZones.length === 0 || !workCalibrationData.pressureMode) return;
    setIsGenerating(true);
    setGeneratingMessage('Refining your work snapshot...');
    setTimeout(() => {
      setIsGenerating(false);
      setCurrentPhase('integrated-work-output');
    }, 2000);
  };
  const handleToggleFriction = (friction: string) => {
    setWorkCalibrationData(prev => ({
      ...prev,
      frictionZones: prev.frictionZones.includes(friction) ? prev.frictionZones.filter(f => f !== friction) : [...prev.frictionZones, friction]
    }));
  };
  if (showLoginModal) {
    return <>
        <Helmet data-id="element-3352">
          <title data-id="element-3353">Quick Work Decode — Login Required | Lazy Monk Labs</title>
        </Helmet>
        <div className="fixed inset-0 bg-[#0A0A0A]/95 backdrop-blur-sm z-50 flex items-center justify-center px-6" data-id="element-3354">
          <div className="max-w-md w-full bg-[#111214] border border-[#2B2B2B] p-8" data-id="element-3355">
            <h2 className="text-2xl font-medium mb-4" data-id="element-3356">Save your snapshot</h2>
            <p className="text-[#F5F5F5]/70 mb-8 leading-relaxed" data-id="element-3357">
              Save your snapshot and return to it anytime.
            </p>
            <button onClick={() => setShowLoginModal(false)} className="w-full px-8 py-4 bg-[#F5F5F5] text-[#0A0A0A] font-semibold text-sm tracking-widest uppercase hover:bg-[#F5F5F5]/90 transition-colors" data-id="element-3358">
              Continue with Google
            </button>
            <button onClick={() => navigate('/decode')} className="w-full mt-4 text-sm text-[#F5F5F5]/50 hover:text-[#F5F5F5]/70 transition-colors" data-id="element-3359">
              Cancel
            </button>
          </div>
        </div>
      </>;
  }
  if (isGenerating) {
    return <>
        <Helmet data-id="element-3360">
          <title data-id="element-3361">Generating Work Snapshot | Lazy Monk Labs</title>
        </Helmet>
        <div className="min-h-screen bg-[#0A0A0A] text-[#F5F5F5] flex items-center justify-center px-6" data-id="element-3362">
          <div className="text-center" data-id="element-3363">
            <div className="mb-6" data-id="element-3364">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-[#F5F5F5]" data-id="element-3365"></div>
            </div>
            <p className="text-lg text-[#F5F5F5]/70" data-id="element-3366">{generatingMessage}</p>
          </div>
        </div>
      </>;
  }
  return <>
      <Helmet data-id="element-3367">
        <title data-id="element-3368">Quick Work Decode | Lazy Monk Labs</title>
      </Helmet>
      <div className="min-h-screen bg-[#0A0A0A] text-[#F5F5F5]" data-id="element-3369">
        <header data-id="element-3370">
          <nav className="fixed top-0 left-0 right-0 z-50 bg-[#0A0A0A]/95 backdrop-blur-sm border-b border-[#2B2B2B]" data-id="element-3371">
            <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between" data-id="element-3372">
              <Link to="/" className="flex items-center gap-3 hover:opacity-80 transition-opacity" data-id="element-3373">
                <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/jXd6KyuddrgAfqwwkkpUrR/Lazy_Monk_Labs_Logo.png" alt="Lazy Monk Labs Logo" className="h-8 w-auto" width="32" height="32" data-id="element-3374" />
                <span className="text-lg font-medium tracking-tight" data-id="element-3375">
                  Lazy Monk Labs
                </span>
              </Link>
              <Link to="/decode" className="text-sm text-[#F5F5F5]/80 hover:text-[#F5F5F5] transition-colors" data-id="element-3376">
                Back to Decode
              </Link>
            </div>
          </nav>
        </header>

        <main className="pt-24 pb-16 px-6" data-id="element-3377">
          <div className="max-w-2xl mx-auto" data-id="element-3378">
            {/* PHASE 1: Baseline Collection (Same as QuickSelfDecode) */}
            {currentPhase === 'baseline-collection' && <div data-id="element-3379">
                <div className="mb-12" data-id="element-3380">
                  <h1 className="text-3xl md:text-4xl font-medium mb-3 leading-tight tracking-tight" data-id="element-3381">
                    Quick Work Decode
                  </h1>
                  <p className="text-sm text-[#F5F5F5]/50" data-id="element-3382">
                    Structural baseline
                  </p>
                </div>

                <div className="space-y-8 mb-12" data-id="element-3383">
                  {/* Date of Birth */}
                  <div data-id="element-3384">
                    <label className="block text-sm font-medium mb-3" data-id="element-3385">
                      Date of birth
                    </label>
                    {baselineData.dateOfBirth && <div className="mb-4 p-4 bg-[#111214] border border-[#2B2B2B] text-center" data-id="element-3386">
                        <div className="text-2xl font-medium" data-id="element-3387">
                          {months.find(m => m.value === baselineData.month)?.label}{' '}
                          {baselineData.day}, {baselineData.year}
                        </div>
                      </div>}
                    <div className="grid grid-cols-3 gap-3" data-id="element-3388">
                      <div data-id="element-3389">
                        <label htmlFor="month" className="block text-xs font-medium mb-2 text-[#F5F5F5]/60 uppercase tracking-wider" data-id="element-3390">
                          Month
                        </label>
                        <select id="month" value={baselineData.month} onChange={e => updateDateOfBirth(baselineData.year, e.target.value, baselineData.day)} className="w-full px-3 py-3 bg-[#0A0A0A] border border-[#2B2B2B] text-[#F5F5F5] text-sm focus:border-[#F5F5F5]/50 focus:outline-none transition-colors" data-id="element-3391">
                          <option value="" data-id="element-3392">Month</option>
                          {months.map(m => <option key={m.value} value={m.value} data-id="element-3393">
                              {m.label}
                            </option>)}
                        </select>
                      </div>
                      <div data-id="element-3394">
                        <label htmlFor="day" className="block text-xs font-medium mb-2 text-[#F5F5F5]/60 uppercase tracking-wider" data-id="element-3395">
                          Day
                        </label>
                        <select id="day" value={baselineData.day} onChange={e => updateDateOfBirth(baselineData.year, baselineData.month, e.target.value)} disabled={!baselineData.month || !baselineData.year} className="w-full px-3 py-3 bg-[#0A0A0A] border border-[#2B2B2B] text-[#F5F5F5] text-sm focus:border-[#F5F5F5]/50 focus:outline-none transition-colors disabled:opacity-30" data-id="element-3396">
                          <option value="" data-id="element-3397">Day</option>
                          {Array.from({
                        length: getDaysInMonth(baselineData.year, baselineData.month)
                      }, (_, i) => i + 1).map(d => <option key={d} value={d.toString().padStart(2, '0')} data-id="element-3398">
                              {d}
                            </option>)}
                        </select>
                      </div>
                      <div data-id="element-3399">
                        <label htmlFor="year" className="block text-xs font-medium mb-2 text-[#F5F5F5]/60 uppercase tracking-wider" data-id="element-3400">
                          Year
                        </label>
                        <select id="year" value={baselineData.year} onChange={e => updateDateOfBirth(e.target.value, baselineData.month, baselineData.day)} className="w-full px-3 py-3 bg-[#0A0A0A] border border-[#2B2B2B] text-[#F5F5F5] text-sm focus:border-[#F5F5F5]/50 focus:outline-none transition-colors" data-id="element-3401">
                          <option value="" data-id="element-3402">Year</option>
                          {years.map(y => <option key={y} value={y} data-id="element-3403">
                              {y}
                            </option>)}
                        </select>
                      </div>
                    </div>
                  </div>

                  {/* Time of Birth */}
                  <div data-id="element-3404">
                    <label className="block text-sm font-medium mb-3" data-id="element-3405">
                      Time of birth{' '}
                      <span className="text-[#F5F5F5]/40" data-id="element-3406">(optional)</span>
                    </label>
                    <div className="space-y-3 mb-4" data-id="element-3407">
                      {[{
                    value: 'exact',
                    label: 'I know the exact time'
                  }, {
                    value: 'approximate',
                    label: 'I know approximately'
                  }, {
                    value: 'not-sure',
                    label: "Not sure / Don't know"
                  }].map(option => <label key={option.value} className={`block border p-4 cursor-pointer transition-colors ${baselineData.timeKnown === option.value ? 'border-[#F5F5F5] bg-[#F5F5F5]/5' : 'border-[#2B2B2B] hover:border-[#F5F5F5]/30'}`} data-id="element-3408">
                          <input type="radio" name="timeKnown" value={option.value} checked={baselineData.timeKnown === option.value} onChange={e => setBaselineData(prev => ({
                      ...prev,
                      timeKnown: e.target.value,
                      timeOfBirth: '',
                      hour: '12',
                      minute: '00',
                      period: 'AM'
                    }))} className="sr-only" data-id="element-3409" />
                          <span className="text-sm" data-id="element-3410">{option.label}</span>
                        </label>)}
                    </div>
                    {(baselineData.timeKnown === 'exact' || baselineData.timeKnown === 'approximate') && <div className="mt-6" data-id="element-3411">
                        <div className="bg-[#111214] border border-[#2B2B2B] p-8" data-id="element-3412">
                          <div className="text-center mb-8" data-id="element-3413">
                            <div className="text-4xl font-medium tracking-tight" data-id="element-3414">
                              {baselineData.hour}:{baselineData.minute}{' '}
                              {baselineData.period}
                            </div>
                            <p className="text-xs text-[#F5F5F5]/40 mt-2" data-id="element-3415">
                              {baselineData.timeKnown === 'approximate' ? 'Your best estimate is fine' : 'Select the exact time'}
                            </p>
                          </div>
                          <div className="mb-6" data-id="element-3416">
                            <label className="block text-xs font-medium mb-3 text-[#F5F5F5]/60 uppercase tracking-wider" data-id="element-3417">
                              Hour
                            </label>
                            <div className="grid grid-cols-6 gap-2" data-id="element-3418">
                              {['12', '01', '02', '03', '04', '05', '06', '07', '08', '09', '10', '11'].map(h => <button key={h} type="button" onClick={() => updateTimeOfBirth(h, baselineData.minute, baselineData.period)} className={`py-3 text-sm font-medium transition-colors ${baselineData.hour === h ? 'bg-[#F5F5F5] text-[#0A0A0A]' : 'bg-[#0A0A0A] border border-[#2B2B2B] hover:border-[#F5F5F5]/30'}`} data-id="element-3419">
                                  {h}
                                </button>)}
                            </div>
                          </div>
                          <div className="mb-6" data-id="element-3420">
                            <label className="block text-xs font-medium mb-3 text-[#F5F5F5]/60 uppercase tracking-wider" data-id="element-3421">
                              Minute
                            </label>
                            <div className="grid grid-cols-6 gap-2" data-id="element-3422">
                              {['00', '15', '30', '45'].map(m => <button key={m} type="button" onClick={() => updateTimeOfBirth(baselineData.hour, m, baselineData.period)} className={`py-3 text-sm font-medium transition-colors ${baselineData.minute === m ? 'bg-[#F5F5F5] text-[#0A0A0A]' : 'bg-[#0A0A0A] border border-[#2B2B2B] hover:border-[#F5F5F5]/30'}`} data-id="element-3423">
                                  {m}
                                </button>)}
                            </div>
                            <div className="mt-2" data-id="element-3424">
                              <input type="number" min="0" max="59" value={baselineData.minute} onChange={e => {
                          const val = e.target.value.padStart(2, '0');
                          if (parseInt(val) >= 0 && parseInt(val) <= 59) updateTimeOfBirth(baselineData.hour, val, baselineData.period);
                        }} placeholder="Or type exact minute" className="w-full px-4 py-2 bg-[#0A0A0A] border border-[#2B2B2B] text-[#F5F5F5] text-sm placeholder-[#F5F5F5]/40 focus:border-[#F5F5F5]/50 focus:outline-none transition-colors" data-id="element-3425" />
                            </div>
                          </div>
                          <div data-id="element-3426">
                            <label className="block text-xs font-medium mb-3 text-[#F5F5F5]/60 uppercase tracking-wider" data-id="element-3427">
                              Period
                            </label>
                            <div className="grid grid-cols-2 gap-2" data-id="element-3428">
                              {['AM', 'PM'].map(p => <button key={p} type="button" onClick={() => updateTimeOfBirth(baselineData.hour, baselineData.minute, p)} className={`py-3 text-sm font-medium transition-colors ${baselineData.period === p ? 'bg-[#F5F5F5] text-[#0A0A0A]' : 'bg-[#0A0A0A] border border-[#2B2B2B] hover:border-[#F5F5F5]/30'}`} data-id="element-3429">
                                  {p}
                                </button>)}
                            </div>
                          </div>
                        </div>
                      </div>}
                  </div>

                  <div data-id="element-3430">
                    <label htmlFor="placeOfBirth" className="block text-sm font-medium mb-2" data-id="element-3431">
                      Place of birth
                    </label>
                    <input type="text" id="placeOfBirth" value={baselineData.placeOfBirth} onChange={e => setBaselineData(prev => ({
                  ...prev,
                  placeOfBirth: e.target.value
                }))} placeholder="City or town" className="w-full px-4 py-3 bg-[#0A0A0A] border border-[#2B2B2B] text-[#F5F5F5] placeholder-[#F5F5F5]/40 focus:border-[#F5F5F5]/50 focus:outline-none transition-colors" data-id="element-3432" />
                  </div>

                  <div data-id="element-3433">
                    <label htmlFor="country" className="block text-sm font-medium mb-2" data-id="element-3434">
                      Country
                    </label>
                    <input type="text" id="country" value={baselineData.country} onChange={e => setBaselineData(prev => ({
                  ...prev,
                  country: e.target.value
                }))} placeholder="Country" className="w-full px-4 py-3 bg-[#0A0A0A] border border-[#2B2B2B] text-[#F5F5F5] placeholder-[#F5F5F5]/40 focus:border-[#F5F5F5]/50 focus:outline-none transition-colors" data-id="element-3435" />
                  </div>
                </div>

                <button onClick={handleBaselineSubmit} disabled={!baselineData.dateOfBirth || !baselineData.placeOfBirth || !baselineData.country || (baselineData.timeKnown === 'exact' || baselineData.timeKnown === 'approximate') && !baselineData.timeOfBirth} className="w-full px-8 py-5 bg-[#F5F5F5] text-[#0A0A0A] font-semibold text-sm tracking-widest uppercase hover:bg-[#F5F5F5]/90 transition-colors shadow-lg disabled:opacity-30 disabled:cursor-not-allowed" data-id="element-3436">
                  Generate Structural Baseline
                </button>
              </div>}

            {/* PHASE 2: Work Baseline Output (Translation) */}
            {currentPhase === 'work-baseline-output' && <div data-id="element-3437">
                <div className="mb-12" data-id="element-3438">
                  <h1 className="text-3xl md:text-4xl font-medium mb-3 leading-tight tracking-tight" data-id="element-3439">
                    Quick Work Decode
                  </h1>
                  <p className="text-sm text-[#F5F5F5]/50" data-id="element-3440">
                    Structural baseline — translated into work context
                  </p>
                </div>

                <div className="mb-12 pb-12 border-b border-[#2B2B2B]" data-id="element-3441">
                  <div className="flex items-start gap-4 mb-4" data-id="element-3442">
                    <span className="text-2xl" data-id="element-3443">🧭</span>
                    <div className="flex-1" data-id="element-3444">
                      <h2 className="text-xl font-medium mb-3" data-id="element-3445">
                        How you naturally operate at work
                      </h2>
                      <p className="text-[#F5F5F5]/70 leading-relaxed" data-id="element-3446">
                        {workBaselineOutput.workOrientation}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="mb-12 pb-12 border-b border-[#2B2B2B]" data-id="element-3447">
                  <div className="flex items-start gap-4 mb-4" data-id="element-3448">
                    <span className="text-2xl" data-id="element-3449">🏗️</span>
                    <div className="flex-1" data-id="element-3450">
                      <h2 className="text-xl font-medium mb-3" data-id="element-3451">
                        How this system responds to authority and pressure
                      </h2>
                      <p className="text-[#F5F5F5]/70 leading-relaxed" data-id="element-3452">
                        {workBaselineOutput.authorityResponse}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="mb-12 pb-12 border-b border-[#2B2B2B]" data-id="element-3453">
                  <div className="flex items-start gap-4 mb-4" data-id="element-3454">
                    <span className="text-2xl" data-id="element-3455">⚠️</span>
                    <div className="flex-1" data-id="element-3456">
                      <h2 className="text-xl font-medium mb-3" data-id="element-3457">
                        Where this pattern creates friction at work
                      </h2>
                      <p className="text-[#F5F5F5]/70 leading-relaxed" data-id="element-3458">
                        {workBaselineOutput.workBlindSpot}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="mb-12 text-center" data-id="element-3459">
                  <p className="text-xs text-[#F5F5F5]/40 uppercase tracking-wider" data-id="element-3460">
                    Structural baseline — translated into work context
                  </p>
                </div>

                <div className="mb-12" data-id="element-3461">
                  <button onClick={() => setShowExplanation(!showExplanation)} className="flex items-center gap-2 text-sm text-[#F5F5F5]/70 hover:text-[#F5F5F5] transition-colors" data-id="element-3462">
                    <span data-id="element-3463">📘</span>
                    <span data-id="element-3464">How this work snapshot was generated</span>
                    <span className="text-xs" data-id="element-3465">
                      {showExplanation ? '▼' : '▶'}
                    </span>
                  </button>
                  {showExplanation && <div className="mt-4 p-6 bg-[#111214] border border-[#2B2B2B]" data-id="element-3466">
                      <p className="text-sm text-[#F5F5F5]/70 leading-relaxed" data-id="element-3467">
                        This snapshot translates your structural baseline into
                        work systems — roles, authority, responsibility, and
                        output. The baseline does not change between life and
                        work. Only the contextual expression does. The next
                        questions tune how this pattern is expressing in your
                        current work environment.
                      </p>
                    </div>}
                </div>

                <div className="space-y-4" data-id="element-3468">
                  <button onClick={() => setCurrentPhase('work-calibration')} className="w-full px-8 py-5 bg-[#F5F5F5] text-[#0A0A0A] font-semibold text-sm tracking-widest uppercase hover:bg-[#F5F5F5]/90 transition-colors shadow-lg" data-id="element-3469">
                    🧩 See how this pattern is expressing at work right now
                  </button>
                  <div className="text-center" data-id="element-3470">
                    <p className="text-xs text-[#F5F5F5]/50 mb-2" data-id="element-3471">
                      Optional · ~30 seconds
                    </p>
                    <p className="text-xs text-[#F5F5F5]/40 leading-relaxed max-w-md mx-auto" data-id="element-3472">
                      This tunes expression, not structure.
                    </p>
                  </div>
                  <button onClick={() => navigate('/decode')} className="w-full px-8 py-4 border border-[#F5F5F5]/30 text-[#F5F5F5]/70 font-medium text-sm tracking-wide hover:bg-[#F5F5F5]/5 transition-colors" data-id="element-3473">
                    Skip for now
                  </button>
                </div>
              </div>}

            {/* PHASE 3: Work Calibration Questions */}
            {currentPhase === 'work-calibration' && <div data-id="element-3474">
                <div className="mb-12" data-id="element-3475">
                  <h1 className="text-3xl md:text-4xl font-medium mb-3 leading-tight tracking-tight" data-id="element-3476">
                    Refine your work snapshot
                  </h1>
                  <p className="text-sm text-[#F5F5F5]/50" data-id="element-3477">~30 seconds</p>
                </div>

                <div className="space-y-12 mb-12" data-id="element-3478">
                  {/* Q1: Work Pressure Level */}
                  <div data-id="element-3479">
                    <h2 className="text-lg font-medium mb-6" data-id="element-3480">
                      How intense does work pressure feel right now?
                    </h2>
                    <div className="space-y-3" data-id="element-3481">
                      {['Low', 'Moderate', 'High', 'Unsustainable'].map(level => <label key={level} className={`block border p-5 cursor-pointer transition-colors ${workCalibrationData.pressureLevel === level ? 'border-[#F5F5F5] bg-[#F5F5F5]/5' : 'border-[#2B2B2B] hover:border-[#F5F5F5]/30'}`} data-id="element-3482">
                            <input type="radio" name="pressureLevel" value={level} checked={workCalibrationData.pressureLevel === level} onChange={e => setWorkCalibrationData(prev => ({
                      ...prev,
                      pressureLevel: e.target.value
                    }))} className="sr-only" data-id="element-3483" />
                            <span className="text-base" data-id="element-3484">{level}</span>
                          </label>)}
                    </div>
                  </div>

                  {/* Q2: Primary Work Friction (Multi-select) */}
                  <div data-id="element-3485">
                    <h2 className="text-lg font-medium mb-3" data-id="element-3486">
                      Where do you feel the most friction at work lately?
                    </h2>
                    <p className="text-sm text-[#F5F5F5]/50 mb-6" data-id="element-3487">
                      Select all that apply
                    </p>
                    <div className="space-y-3" data-id="element-3488">
                      {['Authority / management', 'Ambiguity of role or goals', 'Pace and deadlines', 'Ownership vs control', 'Collaboration / dependency'].map(friction => <label key={friction} className={`block border p-5 cursor-pointer transition-colors ${workCalibrationData.frictionZones.includes(friction) ? 'border-[#F5F5F5] bg-[#F5F5F5]/5' : 'border-[#2B2B2B] hover:border-[#F5F5F5]/30'}`} data-id="element-3489">
                          <input type="checkbox" value={friction} checked={workCalibrationData.frictionZones.includes(friction)} onChange={() => handleToggleFriction(friction)} className="sr-only" data-id="element-3490" />
                          <span className="text-base" data-id="element-3491">{friction}</span>
                        </label>)}
                    </div>
                  </div>

                  {/* Q3: Mode Under Pressure */}
                  <div data-id="element-3492">
                    <h2 className="text-lg font-medium mb-6" data-id="element-3493">
                      When work pressure rises, you usually…
                    </h2>
                    <div className="space-y-3" data-id="element-3494">
                      {['Take more control', 'Pull back and observe', 'Push harder', 'Disengage emotionally'].map(mode => <label key={mode} className={`block border p-5 cursor-pointer transition-colors ${workCalibrationData.pressureMode === mode ? 'border-[#F5F5F5] bg-[#F5F5F5]/5' : 'border-[#2B2B2B] hover:border-[#F5F5F5]/30'}`} data-id="element-3495">
                          <input type="radio" name="pressureMode" value={mode} checked={workCalibrationData.pressureMode === mode} onChange={e => setWorkCalibrationData(prev => ({
                      ...prev,
                      pressureMode: e.target.value
                    }))} className="sr-only" data-id="element-3496" />
                          <span className="text-base" data-id="element-3497">{mode}</span>
                        </label>)}
                    </div>
                  </div>

                  {/* Optional Context */}
                  <div data-id="element-3498">
                    <h2 className="text-lg font-medium mb-3" data-id="element-3499">
                      Anything important about your current work situation?
                    </h2>
                    <p className="text-sm text-[#F5F5F5]/50 mb-4" data-id="element-3500">
                      Optional context
                    </p>
                    <textarea value={workCalibrationData.workContext} onChange={e => setWorkCalibrationData(prev => ({
                  ...prev,
                  workContext: e.target.value
                }))} rows={3} maxLength={200} className="w-full px-4 py-3 bg-[#0A0A0A] border border-[#2B2B2B] text-[#F5F5F5] placeholder-[#F5F5F5]/40 focus:border-[#F5F5F5]/50 focus:outline-none transition-colors resize-none" placeholder="Optional context..." data-id="element-3501" />
                    <p className="text-xs text-[#F5F5F5]/40 mt-2 text-right" data-id="element-3502">
                      {workCalibrationData.workContext.length}/200
                    </p>
                  </div>
                </div>

                <button onClick={handleWorkCalibrationSubmit} disabled={!workCalibrationData.pressureLevel || workCalibrationData.frictionZones.length === 0 || !workCalibrationData.pressureMode} className="w-full px-8 py-5 bg-[#F5F5F5] text-[#0A0A0A] font-semibold text-sm tracking-widest uppercase hover:bg-[#F5F5F5]/90 transition-colors shadow-lg disabled:opacity-30 disabled:cursor-not-allowed" data-id="element-3503">
                  Apply Refinement
                </button>
              </div>}

            {/* PHASE 4: Integrated Work Output */}
            {currentPhase === 'integrated-work-output' && <div data-id="element-3504">
                <div className="mb-12" data-id="element-3505">
                  <h1 className="text-3xl md:text-4xl font-medium mb-3 leading-tight tracking-tight" data-id="element-3506">
                    Quick Work Decode
                  </h1>
                  <p className="text-sm text-[#F5F5F5]/50" data-id="element-3507">Refined Snapshot</p>
                </div>

                <div className="mb-12 pb-12 border-b border-[#2B2B2B]" data-id="element-3508">
                  <div className="flex items-start gap-4 mb-4" data-id="element-3509">
                    <span className="text-2xl" data-id="element-3510">🧱</span>
                    <div className="flex-1" data-id="element-3511">
                      <p className="text-xs text-[#F5F5F5]/40 uppercase tracking-wider mb-3" data-id="element-3512">
                        Structural baseline at work
                      </p>
                      <p className="text-sm text-[#F5F5F5]/60 leading-relaxed" data-id="element-3513">
                        {integratedWorkOutput.structuralBaseline}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="mb-12 pb-12 border-b border-[#2B2B2B]" data-id="element-3514">
                  <div className="flex items-start gap-4 mb-4" data-id="element-3515">
                    <span className="text-2xl" data-id="element-3516">🔄</span>
                    <div className="flex-1" data-id="element-3517">
                      <p className="text-xs text-[#F5F5F5]/40 uppercase tracking-wider mb-3" data-id="element-3518">
                        How this is expressing right now
                      </p>
                      <p className="text-[#F5F5F5]/70 leading-relaxed" data-id="element-3519">
                        {integratedWorkOutput.currentExpression}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="mb-12 pb-12 border-b border-[#2B2B2B]" data-id="element-3520">
                  <div className="flex items-start gap-4 mb-4" data-id="element-3521">
                    <span className="text-2xl" data-id="element-3522">🎯</span>
                    <div className="flex-1" data-id="element-3523">
                      <p className="text-lg font-medium text-[#F5F5F5]/90 italic" data-id="element-3524">
                        "{integratedWorkOutput.workTensionSnapshot}"
                      </p>
                    </div>
                  </div>
                </div>

                <div className="mb-12 pb-12 border-b border-[#2B2B2B]" data-id="element-3525">
                  <button onClick={() => setShowWhatChanged(!showWhatChanged)} className="flex items-center gap-2 text-base font-medium text-[#F5F5F5] hover:text-[#F5F5F5]/80 transition-colors mb-6" data-id="element-3526">
                    <span data-id="element-3527">🔍</span>
                    <span data-id="element-3528">What changed after refinement</span>
                    <span className="text-xs" data-id="element-3529">
                      {showWhatChanged ? '▼' : '▶'}
                    </span>
                  </button>
                  {showWhatChanged && <div className="space-y-6" data-id="element-3530">
                      <div className="p-6 bg-[#111214] border border-[#2B2B2B]" data-id="element-3531">
                        <h3 className="text-sm font-medium mb-4" data-id="element-3532">
                          What baseline showed
                        </h3>
                        <p className="text-sm text-[#F5F5F5]/70 leading-relaxed" data-id="element-3533">
                          {integratedWorkOutput.whatChanged.baseline}
                        </p>
                      </div>
                      <div className="p-6 bg-[#111214] border border-[#2B2B2B]" data-id="element-3534">
                        <h3 className="text-sm font-medium mb-4" data-id="element-3535">
                          What work calibration revealed
                        </h3>
                        <p className="text-sm text-[#F5F5F5]/70 leading-relaxed" data-id="element-3536">
                          {integratedWorkOutput.whatChanged.workContext}
                        </p>
                      </div>
                      <div className="p-6 bg-[#111214] border border-[#2B2B2B]" data-id="element-3537">
                        <h3 className="text-sm font-medium mb-4" data-id="element-3538">
                          Why baseline alone could not show this
                        </h3>
                        <p className="text-sm text-[#F5F5F5]/70 leading-relaxed" data-id="element-3539">
                          {integratedWorkOutput.whatChanged.whyBaselineAlone}
                        </p>
                      </div>
                    </div>}
                </div>

                {/* Deep Work Decode Bridge */}
                <div className="mb-12 pb-12 border-b border-[#2B2B2B]" data-id="element-3540">
                  <h2 className="text-xl font-medium mb-6" data-id="element-3541">
                    What this snapshot does not cover
                  </h2>
                  <div className="space-y-4 text-[#F5F5F5]/70 leading-relaxed" data-id="element-3542">
                    <p data-id="element-3543">
                      This snapshot shows{' '}
                      <strong className="text-[#F5F5F5]" data-id="element-3544">
                        how your structure interacts with work right now
                      </strong>
                      .
                    </p>
                    <p data-id="element-3545">It does not map:</p>
                    <ul className="space-y-2 ml-4" data-id="element-3546">
                      <li data-id="element-3547">• how this pattern developed across your career</li>
                      <li data-id="element-3548">• how it affects leadership, growth, or burnout</li>
                      <li data-id="element-3549">
                        • where work amplifies or suppresses your strengths
                      </li>
                      <li data-id="element-3550">
                        • how role, environment, and authority interact
                        long-term
                      </li>
                    </ul>
                  </div>
                </div>

                <div className="mb-12" data-id="element-3551">
                  <Link to="/report-detail?report=deep-work-decode" className="block w-full px-8 py-5 bg-[#F5F5F5] text-[#0A0A0A] text-center font-semibold text-sm tracking-widest uppercase hover:bg-[#F5F5F5]/90 transition-colors shadow-lg" data-id="element-3552">
                    Explore Deep Work Decode
                  </Link>
                  <p className="text-xs text-[#F5F5F5]/40 text-center mt-3" data-id="element-3553">
                    A full diagnostic of your work-pattern architecture
                  </p>
                </div>

                {/* Exit Options */}
                <div className="space-y-4 pt-8 border-t border-[#2B2B2B]" data-id="element-3554">
                  <button onClick={() => navigate('/decode')} className="w-full px-8 py-4 border border-[#F5F5F5]/30 text-[#F5F5F5]/70 font-medium text-sm tracking-wide hover:bg-[#F5F5F5]/5 transition-colors" data-id="element-3555">
                    💾 Save Snapshot
                  </button>
                  <button onClick={() => {
                setCurrentPhase('work-baseline-output');
                setWorkCalibrationData({
                  pressureLevel: '',
                  frictionZones: [],
                  pressureMode: '',
                  workContext: ''
                });
              }} className="w-full px-8 py-4 text-sm text-[#F5F5F5]/50 hover:text-[#F5F5F5]/70 transition-colors" data-id="element-3556">
                    🔁 Run another Quick Decode
                  </button>
                  <Link to="/report-detail?report=deep-self-decode" className="block w-full px-8 py-4 text-sm text-[#F5F5F5]/50 hover:text-[#F5F5F5]/70 transition-colors text-center" data-id="element-3557">
                    ➡️ Explore Deep Self Decode
                  </Link>
                </div>
              </div>}
          </div>
        </main>
      </div>
    </>;
}